/**
 * AdminContent — Phase A-7
 * 4 onglets : Textes / Citations / Données de référence / (readonly info).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Quote as QuoteIcon, Database, PenSquare, Plus, Trash2,
  Save, X, RefreshCw, AlertTriangle, Scale,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  fetchContentList, patchContent,
  fetchQuotes, addQuote, patchQuote, deleteQuote,
  fetchCountries, addCountry, patchCountry,
  fetchProfiles, addProfile, patchProfile,
  fetchSectors, patchSector, rebalanceSectors,
} from "@/lib/admin-api";
import { invalidateDynamicTextCache } from "@/components/DynamicText";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");
const SECTIONS = {
  accueil: "Page d'accueil",
  about: "À propos",
  footer: "Footer",
  emails: "Emails",
  legal: "Avertissements légaux",
  dashboard: "Tableau de bord",
};

export default function AdminContent() {
  const [tab, setTab] = useState("texts");
  return (
    <div className="space-y-4" data-testid="admin-content-page">
      <div className="flex items-center gap-2">
        <PenSquare className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Contenu &amp; Données</h1>
      </div>

      <div className="flex gap-2 border-b">
        {[
          ["texts", "Textes", FileText],
          ["quotes", "Citations", QuoteIcon],
          ["reference", "Données de référence", Database],
        ].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition ${
              tab === k ? "border-b-2 border-red-700 font-semibold text-red-700 dark:text-red-400"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`content-tab-${k}`}>
            <Icon className="h-4 w-4" />{l}
          </button>
        ))}
      </div>

      {tab === "texts" && <TextsTab />}
      {tab === "quotes" && <QuotesTab />}
      {tab === "reference" && <ReferenceTab />}
    </div>
  );
}

/* ================== TEXTS ================== */
function TextsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  async function load() {
    try { setRows(await fetchContentList()); }
    catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      const s = r.section || "autre";
      (g[s] ||= []).push(r);
    }
    return g;
  }, [rows]);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([section, items]) => (
        <Card key={section}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{SECTIONS[section] || section}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-xs" data-testid={`texts-table-${section}`}>
              <thead>
                <tr className="border-b text-left uppercase text-muted-foreground">
                  <th className="px-3 py-2">Clé</th>
                  <th className="px-3 py-2">Aperçu FR</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Modifié</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.key} className="border-b">
                    <td className="px-3 py-2 font-mono">{r.key}</td>
                    <td className="px-3 py-2 max-w-md truncate" title={r.content_fr}>{r.content_fr || "—"}</td>
                    <td className="px-3 py-2"><Badge variant="outline">{r.content_type}</Badge></td>
                    <td className="px-3 py-2 text-[10px]">{fmtDate(r.updated_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`text-edit-${r.key}`}>
                        <PenSquare className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
      {editing && (
        <ContentEditor row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ContentEditor({ row, onClose, onSaved }) {
  const [lang, setLang] = useState("fr");
  const [form, setForm] = useState({
    content_fr: row.content_fr || "",
    content_ar: row.content_ar || "",
    content_en: row.content_en || "",
    content_type: row.content_type || "text",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await patchContent(row.key, form);
      invalidateDynamicTextCache(row.key);
      toast.success("Contenu enregistré.");
      onSaved();
    } catch { toast.error("Échec."); }
    finally { setSaving(false); }
  }

  const val = form[`content_${lang}`];
  const setVal = (v) => setForm((f) => ({ ...f, [`content_${lang}`]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="content-editor">
        <DialogHeader>
          <DialogTitle>Éditer « {row.key} »</DialogTitle>
          <DialogDescription>Section : {SECTIONS[row.section] || row.section}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 border-b">
          {[["fr", "🇫🇷 Français"], ["ar", "🇹🇳 العربية"], ["en", "🇬🇧 English"]].map(([k, l]) => (
            <button key={k} onClick={() => setLang(k)}
              className={`px-3 py-1 text-xs ${lang === k ? "border-b-2 border-red-700 font-semibold" : "text-muted-foreground"}`}
              data-testid={`lang-${k}`}>{l}</button>
          ))}
        </div>
        <div className="space-y-3 py-2">
          {form.content_type === "text" && (
            <Textarea rows={4} value={val} onChange={(e) => setVal(e.target.value)} data-testid="content-text" />
          )}
          {form.content_type === "html" && (
            <>
              <Textarea rows={6} value={val} onChange={(e) => setVal(e.target.value)}
                placeholder="HTML simple : <p>, <b>, <i>, <a href>" data-testid="content-html" />
              <div className="rounded border bg-muted/30 p-3 text-sm">
                <div className="mb-1 text-[10px] uppercase text-muted-foreground">Aperçu</div>
                <div dangerouslySetInnerHTML={{ __html: val }} />
              </div>
            </>
          )}
          {form.content_type === "quote" && (
            <Textarea rows={3} value={val} onChange={(e) => setVal(e.target.value)}
              placeholder="« Citation » — Auteur" data-testid="content-quote" />
          )}
          {form.content_type === "list" && (
            <Textarea rows={5} value={val} onChange={(e) => setVal(e.target.value)}
              placeholder="Un item par ligne" data-testid="content-list" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="mr-1 h-4 w-4" />Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="content-save">
            <Save className="mr-1 h-4 w-4" />{saving ? "…" : "Sauvegarder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================== QUOTES ================== */
function QuotesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    try { setRows(await fetchQuotes()); }
    catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  async function remove(id) {
    if (!confirm("Supprimer cette citation ?")) return;
    try { await deleteQuote(id); toast.success("Supprimée."); load(); }
    catch { toast.error("Échec."); }
  }

  return (
    <div className="space-y-4">
      <Button onClick={() => setCreating(true)} data-testid="quote-add-btn">
        <Plus className="mr-1 h-4 w-4" /> Ajouter une citation
      </Button>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="quotes-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Texte</th>
                <th className="px-3 py-2">Auteur</th>
                <th className="px-3 py-2">Chapitre</th>
                <th className="px-3 py-2">Section</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.id} className="border-b" data-testid={`quote-row-${q.id}`}>
                  <td className="px-3 py-2 max-w-md truncate italic" title={q.text}>« {q.text} »</td>
                  <td className="px-3 py-2">{q.author}</td>
                  <td className="px-3 py-2 text-[10px]">{q.source_chapter || "—"} {q.pages || ""}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{q.display_section}</Badge></td>
                  <td className="px-3 py-2">
                    {q.is_active ? <Badge className="bg-emerald-600 text-white">Active</Badge>
                      : <Badge variant="outline">Inactive</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(q)} data-testid={`quote-edit-${q.id}`}>
                      <PenSquare className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(q.id)} data-testid={`quote-del-${q.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Aucune citation.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {(editing || creating) && (
        <QuoteEditor
          initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function QuoteEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    text: "", author: "Ahmed ELY Mustapha", source_chapter: "",
    pages: "", display_section: "random", is_active: true,
  });
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      if (initial) await patchQuote(initial.id, form);
      else await addQuote(form);
      toast.success("Enregistré.");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
    finally { setSaving(false); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="quote-editor">
        <DialogHeader><DialogTitle>{initial ? "Modifier" : "Nouvelle"} citation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Texte (max 300)</Label>
            <Textarea maxLength={300} rows={3} value={form.text}
              onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} data-testid="qe-text" />
            <div className="text-right text-[10px] text-muted-foreground">{form.text.length}/300</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Auteur</Label>
              <Input value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))} data-testid="qe-author" /></div>
            <div><Label>Chapitre</Label>
              <Input value={form.source_chapter || ""} onChange={(e) => setForm((f) => ({ ...f, source_chapter: e.target.value }))} data-testid="qe-chapter" /></div>
            <div><Label>Pages</Label>
              <Input value={form.pages || ""} onChange={(e) => setForm((f) => ({ ...f, pages: e.target.value }))} data-testid="qe-pages" /></div>
            <div><Label>Section</Label>
              <select className="h-10 w-full rounded-md border px-3 text-sm" value={form.display_section}
                onChange={(e) => setForm((f) => ({ ...f, display_section: e.target.value }))} data-testid="qe-section">
                {["accueil", "dashboard", "rapport", "jugement", "random"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} data-testid="qe-active" />
            Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="qe-save">{saving ? "…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================== REFERENCE ================== */
function ReferenceTab() {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <CountriesPanel />
      <ProfilesPanel />
      <SectorsPanel />
    </div>
  );
}

function CountriesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  async function load() { try { setRows(await fetchCountries()); } catch { toast.error("Erreur."); } }
  useEffect(() => { load(); }, []);
  async function toggle(c) {
    try { await patchCountry(c.id, { ...c, is_active: !c.is_active }); load(); }
    catch { toast.error("Échec."); }
  }
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">Pays &amp; Monnaies ({rows.length})</CardTitle>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="country-add"><Plus className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs" data-testid="countries-table">
          <thead><tr className="border-b text-left uppercase text-muted-foreground">
            <th className="px-2 py-1">Code</th><th className="px-2 py-1">Nom</th><th className="px-2 py-1">Monnaie</th>
            <th className="px-2 py-1">Actif</th><th className="px-2 py-1"></th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b">
                <td className="px-2 py-1 font-mono font-bold">{c.code}</td>
                <td className="px-2 py-1">{c.name}</td>
                <td className="px-2 py-1 font-mono">{c.currency_code} {c.currency_symbol}</td>
                <td className="px-2 py-1">
                  <button onClick={() => toggle(c)} data-testid={`country-toggle-${c.id}`}>
                    {c.is_active ? <Badge className="bg-emerald-600 text-white">ON</Badge>
                      : <Badge variant="outline">OFF</Badge>}
                  </button>
                </td>
                <td className="px-2 py-1 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)} data-testid={`country-edit-${c.id}`}>
                    <PenSquare className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      {(editing || creating) && (
        <CountryEditor initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </Card>
  );
}

function CountryEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    code: "", name: "", currency_code: "", currency_symbol: "", is_active: true,
  });
  async function save() {
    try {
      if (initial) await patchCountry(initial.id, form);
      else await addCountry(form);
      toast.success("Enregistré.");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="country-editor">
        <DialogHeader><DialogTitle>{initial ? "Modifier" : "Nouveau"} pays</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Code (ISO 2-3)</Label><Input value={form.code} maxLength={3}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} data-testid="ce-code" /></div>
          <div><Label>Nom</Label><Input value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="ce-name" /></div>
          <div><Label>Code monnaie</Label><Input value={form.currency_code} maxLength={5}
            onChange={(e) => setForm((f) => ({ ...f, currency_code: e.target.value.toUpperCase() }))} data-testid="ce-ccode" /></div>
          <div><Label>Symbole</Label><Input value={form.currency_symbol}
            onChange={(e) => setForm((f) => ({ ...f, currency_symbol: e.target.value }))} data-testid="ce-csymbol" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} data-testid="ce-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfilesPanel() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  async function load() { try { setRows(await fetchProfiles()); } catch { toast.error("Erreur."); } }
  useEffect(() => { load(); }, []);
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm">Profils contribuables ({rows.length})</CardTitle>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="profile-add"><Plus className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs" data-testid="profiles-table">
          <thead><tr className="border-b text-left uppercase text-muted-foreground">
            <th className="px-2 py-1">Nom</th><th className="px-2 py-1 text-right">Salaire</th>
            <th className="px-2 py-1 text-right">IR</th><th className="px-2 py-1 text-right">CS</th>
            <th className="px-2 py-1">Pays</th><th className="px-2 py-1"></th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-b">
                <td className="px-2 py-1">{p.name}</td>
                <td className="px-2 py-1 text-right font-mono">{p.monthly_salary.toLocaleString("fr-FR")}</td>
                <td className="px-2 py-1 text-right font-mono">{(p.income_tax_rate * 100).toFixed(1)}%</td>
                <td className="px-2 py-1 text-right font-mono">{(p.social_contributions_rate * 100).toFixed(1)}%</td>
                <td className="px-2 py-1"><Badge variant="outline">{p.country_code}</Badge></td>
                <td className="px-2 py-1 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`profile-edit-${p.id}`}>
                    <PenSquare className="h-3 w-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
      {(editing || creating) && (
        <ProfileEditor initial={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </Card>
  );
}

function ProfileEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", monthly_salary: 1000, income_tax_rate: 0.1,
    social_contributions_rate: 0.08, country_code: "TN", is_active: true,
  });
  async function save() {
    try {
      const payload = {
        ...form,
        monthly_salary: Number(form.monthly_salary),
        income_tax_rate: Number(form.income_tax_rate),
        social_contributions_rate: Number(form.social_contributions_rate),
      };
      if (initial) await patchProfile(initial.id, payload);
      else await addProfile(payload);
      toast.success("Enregistré."); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }
  // Preview V1 : jours sacrifiés par an (approximation simple)
  const annualGross = Number(form.monthly_salary) * 12 || 0;
  const taxes = annualGross * (Number(form.income_tax_rate) + Number(form.social_contributions_rate));
  const v1Days = Math.round((taxes / (annualGross || 1)) * 365);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="profile-editor">
        <DialogHeader><DialogTitle>{initial ? "Modifier" : "Nouveau"} profil</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label>Nom du profil</Label><Input value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="pe-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Salaire mensuel</Label><Input type="number" value={form.monthly_salary}
              onChange={(e) => setForm((f) => ({ ...f, monthly_salary: e.target.value }))} data-testid="pe-salary" /></div>
            <div><Label>Pays</Label><Input value={form.country_code} maxLength={3}
              onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value.toUpperCase() }))} data-testid="pe-country" /></div>
            <div><Label>Taux IR (0-1)</Label><Input type="number" step="0.001" min="0" max="1" value={form.income_tax_rate}
              onChange={(e) => setForm((f) => ({ ...f, income_tax_rate: e.target.value }))} data-testid="pe-ir" /></div>
            <div><Label>Cotisations (0-1)</Label><Input type="number" step="0.001" min="0" max="1" value={form.social_contributions_rate}
              onChange={(e) => setForm((f) => ({ ...f, social_contributions_rate: e.target.value }))} data-testid="pe-cs" /></div>
          </div>
          <div className="rounded border bg-amber-50 p-2 text-xs dark:bg-amber-950/20" data-testid="pe-preview">
            Avec ces paramètres, ce profil sacrifie environ <b>{v1Days} jours/an</b> en prélèvements.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} data-testid="pe-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SectorsPanel() {
  const [rows, setRows] = useState([]);
  const [activeSum, setActiveSum] = useState(0);
  const [saving, setSaving] = useState(null);

  async function load() {
    try {
      const r = await fetchSectors();
      setRows(r.data || []);
      setActiveSum(r.active_sum || 0);
    } catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  async function updateSector(s, patch) {
    setSaving(s.id);
    try { await patchSector(s.id, { ...s, ...patch }); await load(); }
    catch { toast.error("Échec."); }
    finally { setSaving(null); }
  }
  async function rebalance() {
    try { await rebalanceSectors(); toast.success("Rééquilibré."); load(); }
    catch { toast.error("Échec."); }
  }

  const sumBad = Math.abs(activeSum - 1) > 0.001;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Scale className="h-4 w-4" /> Secteurs vitalistes
        </CardTitle>
        <Button size="sm" variant="outline" onClick={rebalance} data-testid="sectors-rebalance">
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Rééquilibrer
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className={`rounded border p-2 text-xs ${sumBad ? "border-red-400 bg-red-50 dark:bg-red-950/20" : "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20"}`}
          data-testid="sectors-sum">
          {sumBad && <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-red-600" />}
          Somme des wᵢ actifs : <b className="font-mono">{activeSum.toFixed(4)}</b>
          {sumBad ? " — doit être 1.00" : " ✓"}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="sectors-table">
            <thead><tr className="border-b text-left uppercase text-muted-foreground">
              <th className="px-2 py-1">Secteur</th><th className="px-2 py-1">wᵢ</th>
              <th className="px-2 py-1">Ordre</th><th className="px-2 py-1">Actif</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b">
                  <td className="px-2 py-1">{s.name}</td>
                  <td className="px-2 py-1">
                    <Input type="number" step="0.01" min="0" max="1" defaultValue={s.weight}
                      className="h-7 w-20" disabled={saving === s.id}
                      onBlur={(e) => {
                        const v = Number(e.target.value);
                        if (v !== s.weight) updateSector(s, { weight: v });
                      }}
                      data-testid={`sector-weight-${s.id}`} />
                  </td>
                  <td className="px-2 py-1 font-mono">{s.display_order}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => updateSector(s, { is_active: !s.is_active })}
                      data-testid={`sector-toggle-${s.id}`}>
                      {s.is_active ? <Badge className="bg-emerald-600 text-white">ON</Badge>
                        : <Badge variant="outline">OFF</Badge>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
